class KeyVisualCarousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[id^="KvTrack-"]');
    this.slides = Array.from(root.querySelectorAll('.kv-carousel__slide'));
    this.dots = Array.from(root.querySelectorAll('[data-kv-dot]'));
    this.prevBtn = root.querySelector('[data-kv-prev]');
    this.nextBtn = root.querySelector('[data-kv-next]');

    this.current = 0;
    this.total = this.slides.length;
    this.isSlideMode = root.dataset.transition === 'slide';
    this.autoplay = root.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(root.dataset.autoplaySpeed, 10) || 5000;
    this.timer = null;

    // Measure the real, rendered image dimensions so the carousel's height
    // matches the actual picture exactly (no gaps, no guessing from metadata).
    this.measureImages();
    this.updateRatio();

    // Keep the editor preview in sync with whichever slide is being edited.
    this.bindEditorEvents();

    if (this.total <= 1) return;

    this.bindEvents();
    this.startAutoplay();
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.goTo(this.current - 1);
        this.restartAutoplay();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.goTo(this.current + 1);
        this.restartAutoplay();
      });
    }

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.slideIndex, 10);
        this.goTo(index);
        this.restartAutoplay();
      });
    });

    this.root.addEventListener('mouseenter', () => this.stopAutoplay());
    this.root.addEventListener('mouseleave', () => this.startAutoplay());

    // Touch swipe support
    let touchStartX = 0;
    let touchEndX = 0;

    this.root.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    this.root.addEventListener(
      'touchend',
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const delta = touchEndX - touchStartX;
        if (Math.abs(delta) > 40) {
          if (delta < 0) {
            this.goTo(this.current + 1);
          } else {
            this.goTo(this.current - 1);
          }
          this.restartAutoplay();
        }
      },
      { passive: true }
    );

    // Pause when tab not visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    });
  }

  // Sync the visible slide with whichever block the merchant is currently
  // editing in the Shopify theme editor, and pause autoplay while they work.
  bindEditorEvents() {
    document.addEventListener('shopify:block:select', (event) => {
      const index = this.slides.indexOf(event.target);
      if (index === -1) return;
      this.stopAutoplay();
      this.goTo(index);
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const index = this.slides.indexOf(event.target);
      if (index === -1) return;
      this.startAutoplay();
    });
  }

  // Read each slide's actual <img> pixel dimensions once loaded, and use
  // that (rather than only the CMS's stored metadata) as the source of
  // truth for the aspect ratio, so the container fits with no top/bottom gap.
  measureImages() {
    this.slides.forEach((slide) => {
      const desktopImg = slide.querySelector('.kv-carousel__img--desktop');
      const mobileImg = slide.querySelector('.kv-carousel__img--mobile');
      const hasMobileImage = slide.dataset.hasMobileImage === 'true';

      const applyDesktopRatio = () => {
        if (!desktopImg || !desktopImg.naturalWidth || !desktopImg.naturalHeight) return;
        const ratio = (desktopImg.naturalWidth / desktopImg.naturalHeight).toFixed(4);
        slide.dataset.ratioDesktop = ratio;
        if (!hasMobileImage) slide.dataset.ratioMobile = ratio;
        if (slide === this.slides[this.current]) this.updateRatio();
      };

      const applyMobileRatio = () => {
        if (!mobileImg || !mobileImg.naturalWidth || !mobileImg.naturalHeight) return;
        const ratio = (mobileImg.naturalWidth / mobileImg.naturalHeight).toFixed(4);
        slide.dataset.ratioMobile = ratio;
        if (slide === this.slides[this.current]) this.updateRatio();
      };

      if (desktopImg) {
        if (desktopImg.complete) applyDesktopRatio();
        else desktopImg.addEventListener('load', applyDesktopRatio, { once: true });
      }

      if (mobileImg) {
        if (mobileImg.complete) applyMobileRatio();
        else mobileImg.addEventListener('load', applyMobileRatio, { once: true });
      }
    });
  }

  goTo(index) {
    const nextIndex = (index + this.total) % this.total;
    const prevIndex = this.current;

    if (nextIndex === prevIndex) return;

    this.slides[prevIndex].classList.remove('is-active');
    this.slides[prevIndex].setAttribute('aria-hidden', 'true');

    if (this.isSlideMode) {
      this.slides[prevIndex].classList.toggle('is-prev', nextIndex < prevIndex || (prevIndex === this.total - 1 && nextIndex === 0));
    }

    this.slides[nextIndex].classList.add('is-active');
    this.slides[nextIndex].classList.remove('is-prev');
    this.slides[nextIndex].removeAttribute('aria-hidden');

    if (this.dots[prevIndex]) {
      this.dots[prevIndex].classList.remove('is-active');
      this.dots[prevIndex].setAttribute('aria-selected', 'false');
    }
    if (this.dots[nextIndex]) {
      this.dots[nextIndex].classList.add('is-active');
      this.dots[nextIndex].setAttribute('aria-selected', 'true');
    }

    this.current = nextIndex;
    this.updateRatio();

    // clean up prev-state class after transition
    window.setTimeout(() => {
      this.slides.forEach((slide, i) => {
        if (i !== this.current) slide.classList.remove('is-prev');
      });
    }, 700);
  }

  // Match the container's aspect ratio to whichever slide is now active,
  // so a tall image makes the carousel tall and a wide image makes it wide.
  updateRatio() {
    const slide = this.slides[this.current];
    if (!slide) return;
    const ratioDesktop = slide.dataset.ratioDesktop || this.root.dataset.fallbackRatioDesktop;
    const ratioMobile = slide.dataset.ratioMobile || this.root.dataset.fallbackRatioMobile;
    if (ratioDesktop) this.root.style.setProperty('--kv-ratio-desktop', ratioDesktop);
    if (ratioMobile) this.root.style.setProperty('--kv-ratio-mobile', ratioMobile);
  }

  startAutoplay() {
    if (!this.autoplay || this.total <= 1) return;
    this.stopAutoplay();
    this.timer = window.setInterval(() => {
      this.goTo(this.current + 1);
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

// Initialize every carousel instance found on the page.
function initKeyVisualCarousels() {
  document.querySelectorAll('.kv-carousel').forEach((el) => {
    if (el.dataset.kvInitialized) return;
    el.dataset.kvInitialized = 'true';
    new KeyVisualCarousel(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initKeyVisualCarousels);
} else {
  initKeyVisualCarousels();
}

// Re-init on Shopify theme editor section load (for live preview in customizer)
document.addEventListener('shopify:section:load', (event) => {
  const carousel = event.target.querySelector('.kv-carousel');
  if (carousel) {
    delete carousel.dataset.kvInitialized;
    initKeyVisualCarousels();
  }
});
